/**
 * ── "DISPLAY AS" — the display affordances on a metric's library row ────────────────
 *
 * A metric row in the Layout panel can be dragged onto the canvas as a METRIC. This row
 * beneath it says which OTHER displays the same metric supports — Chart, Map, Table —
 * and lets the reader drag or click one of those instead.
 *
 * ## "Display as", not "Also as" (VRG-481), and not "Show as" (VERA-260)
 *
 * "Also as" was only nameable while a PRIMARY existed: the library used to carry a
 * separate row per `(metric, display)` pair, so `Total Scans` was the metric row and
 * `Total Scans over time` was another row, and these chips offered the *other* rows. One
 * row per metric removes the primary, and with it the thing the others were "also" to.
 *
 * The verb matters too. These are not "Add as" — toggling Chart off does not un-add a
 * thing, it stops showing a representation of a metric that is still in the library.
 * "Add" is right only while the chips create separate library entries, which is exactly
 * what this change removes.
 *
 * "Display as" over "Show as" because the toolbar's own display filter is already called
 * `Display as` (`AnalyticsCustomize`'s `allTypes`), and two controls naming the same axis
 * two different ways is the ambiguity that label was chosen to end. This was the missed
 * call site of that rename, not a second decision.
 *
 * ## THE LABEL OWNS ITS OWN LINE — a layout invariant, not a preference
 *
 * The label and its info icon sit on one line; the chips flow beneath. Inline they did
 * not fit. The card's content box is 264px (a 340px panel, less 16px x 2 of panel
 * padding and 8px x 2 of card padding), and inline the row measured
 *
 *   "Show as" 49.6 + 8 + info 14 + 8 + Chart 65.3 + 8 + Map 58.8  =  211.7px
 *
 * leaving 52.3px for a `Table` chip that needs 70.7px — so `Table` wrapped alone onto a
 * second line, indented by the Stack's own margin. Renaming to "Display as" costs a
 * further 11.8px at 14px Lato, which makes it worse rather than better.
 *
 * ⚠️ DO NOT "fix" this by widening the panel. It does not scale, and the arithmetic says
 * so: fitting three chips inline needs a ~375px panel today, and each new display type
 * costs another ~70-89px, so five options would need ~514px — half the canvas, spent to
 * keep a label on one line. `table` has just landed and sparkline / funnel / gauge are
 * queued. With the label on its own line the chips get the full content column, which fits
 * all three today on ONE line (widest row: 81.3 + 8 + 74.8 + 8 + 62.7 = 234.8px) and
 * degrades to a tidy two-line chip block at five or six — never an orphan.
 *
 * ⚠️ THE PANEL DID LATER GROW — 340 → 393, so this column is 317px rather than the 264 the
 * measurements above were taken at — and that does NOT overturn the paragraph. It was
 * bought for a different problem (the SOLUTION badges, a second wrapping chip row this
 * component knew nothing about) and it is one column, not the 111px an inline label would
 * have needed. The 234.8px arrangement fits at either width. See
 * `ANALYTICS_CUSTOMIZE_PANEL_WIDTH` for what the canvas paid and for why the next column
 * is not available.
 *
 * This is also just the house field pattern (§31): a label above its control, 8px gap.
 *
 * ⚠️ The chip container uses CSS `gap` (`useFlexGap`), NOT `spacing`. MUI's `spacing`
 * compiles to `margin-left` on siblings, which a wrapped chip CARRIES onto the next line
 * — that is the 8px indent `Table` showed. `gap` is not applied at a line start, so
 * wrapped rows align flush with the first.
 *
 * ⚠️ `metric` is deliberately NOT a chip here, and the spec's sketch shows it as one.
 * The refinement that supersedes the sketch is the hide rule below, and it is stated in
 * terms of the ALTERNATES ("hide when NO display is available" — 32 of 36 affordances,
 * "two greyed chips"). `metric` is unconditionally capable, so a row containing it could
 * never hide and that rule would be unimplementable. The metric toggle is the row's own
 * `+`/`X` control, one level up in `AnalyticsCustomize`.
 *
 * ## Capability is stated BEFORE the drag, which is why there is no drop-time error
 *
 * An incapable display renders as a DISABLED affordance carrying the reason. It is not
 * draggable and its click target is inert, so an impossible drop cannot be attempted in
 * the first place.
 *
 * An error raised DURING a drag was considered and rejected: it is a late explanation for
 * something that should have been legible before the gesture started, and by the time it
 * fires the reader has already committed to an intent the product then refuses. Stating
 * it up front costs one line per row and removes the failure mode entirely.
 *
 * ## The ROW hides when NOTHING is available; a CHIP stays when a sibling is (VRG-481)
 *
 * These are two different questions and they get two different answers, because the two
 * cases communicate different things:
 *
 *   ALL unavailable  -> the row says nothing but "absence". Today that is **32 of the 36**
 *                       affordances (18 metrics x 2 displays), so almost every row in the
 *                       library would carry a dead row whose entire content is two greyed
 *                       chips, repeated down a scrolling list. The whole row is dropped.
 *   SOME available   -> the disabled chip is INFORMATIVE. `Chart` live beside `Map` greyed
 *                       tells a reader this metric has a dated history but no location —
 *                       a real fact about the data, not discoverable anywhere else on this
 *                       page. The chip stays, with its tooltip.
 *
 * ⚠️ THE RULE IS ABSOLUTE IN BOTH DIRECTIONS. "No row" only means "this metric has no
 * other displays" if it is never used for anything else — so do NOT hide a row with one
 * live chip on the grounds that it looks sparse, and do not hide a lone greyed chip that
 * has a live sibling. The moment it is applied by eye it stops carrying information.
 *
 * ## Disabled, not hidden — and this is the OPPOSITE of what §46 usually says
 *
 * §46 splits an unavailable control three ways and would, read mechanically, put this in
 * the HIDE bucket: it is not a permission and not an in-flight mutation, so it looks like
 * record state. The axis §46 actually turns on is WHO OWNS THE RESOLUTION, and that is
 * what decides it here.
 *
 * A record-state block is hidden because the user resolves it themselves, usually one
 * action away, and the app owes them the RESOLVING action rather than a tombstone. A
 * permission block stays visible because someone ELSE resolves it, asynchronously, and
 * the disabled control is the only channel that can name what to ask for.
 *
 * Capability is a third case §46 does not enumerate: **nobody resolves it.** It is a
 * property of what Vera records, and no user action — theirs or an admin's — changes it
 * this quarter. Hiding it would leave a reader comparing two rows, seeing one offer a
 * chart and the other not, with no way to learn whether that is a limitation, a bug, or
 * something they configured wrong. So the affordance stays and says which.
 *
 * ⚠️ Which makes the tooltip's WORDING the load-bearing part, and it must not imply an
 * action. "Ask an admin" would be a false promise; "add your figures" would be worse,
 * because this page already has a state where that IS the answer (`needs_input`) and
 * confusing the two is precisely the defect `useRoiCards` documents at length. The
 * sentences come from `analyticsDisplayCapability`, which is where that discipline is
 * enforced — see its header.
 *
 * ## Both ways in, same as the row above
 *
 * Every enabled affordance is draggable AND clickable. Drag-only would be unreachable by
 * keyboard, invisible to anyone who does not think to try it, and unverifiable without a
 * live renderer. The click is the accessible path; the drag is the one that lets you
 * choose where it lands.
 *
 * ## ⚠️ TWO DRAG TARGETS IN ONE ROW, and how a reader tells them apart (VERA-260)
 *
 * A library row already has a drag handle: the always-visible `⠿` in its left gutter, which
 * drags the WHOLE ROW onto the canvas as a metric tile. These chips add a second, and
 * "two identical-looking drags" would be a worse defect than the undiscoverable one this
 * change set out to fix. Four things separate them, and they are separated on purpose:
 *
 *   WHERE     the row's grip is OUTSIDE the content column, in its own gutter, at the
 *             row's left edge. A chip's grip is INSIDE a bordered pill, in the row's last
 *             block, under a label that says `Display as`.
 *   WHEN      the row's grip is ALWAYS visible — it is chrome, and it names the row's
 *             default gesture. A chip's grip appears only under the pointer or on focus,
 *             so at most ONE grip is emphasised at a time: the one you are pointing at.
 *   WHAT      the row's grip has no tooltip because dragging a row is the panel's premise.
 *             A chip's tooltip names the outcome — "Add Total Scans as a chart — drag to
 *             place it".
 *   SIZE      16px at `text.disabled` versus 13px inheriting the chip's own ink.
 *
 * The load-bearing one is WHEN. A grip that materialises under the cursor reads as "this
 * specific thing, the one you are touching", which is exactly the disambiguation needed;
 * a second always-on grip would have read as a duplicate of the first. If the chip grip is
 * ever made permanent, this distinction goes with it.
 */
import React, { useCallback, useMemo } from 'react'

import { Box, Stack, Tooltip, Typography, alpha, useTheme } from '@mui/material'
import { GripVerticalIcon, XIcon } from 'lucide-react'

import { AnalyticsCategoryGlyph } from 'components/display/AnalyticsCategoryLabel'
import { InfoTooltip } from 'components/feedback/InfoTooltip'

import {
  analyticsDefaultVariantCardId,
  analyticsDisplayCapability,
  analyticsDisplayLabel,
  type AnalyticsDisplay,
} from './analyticsDisplay'
// The id grammar lives in `analyticsGrid` (a leaf module) so the seeds can build a variant
// id without closing an import cycle. See its own note.
import { isMetricDrawnAsDisplay } from './analyticsGrid'
// The chip's geometry, shared with the solution badges in the row just above (§36).
import { ANALYTICS_CHIP_GAP, ANALYTICS_CHIP_SX } from './analyticsChip'

/**
 * The displays a metric can be drawn AS. `metric` is excluded deliberately.
 *
 * The row this sits under IS the metric display — it carries the metric's own title, its
 * "Metric" type label and its own add/remove control. Repeating it here as a third
 * affordance would be a second control for a thing already on screen, and it is the one
 * entry whose answer is unconditionally "yes", so it would also be the one nobody reads.
 */
const ALTERNATE_DISPLAYS: Exclude<AnalyticsDisplay, 'metric'>[] = [
  'chart',
  'map',
  // VERA-260. The row's hide rule ("hide when NO display is available") is stated in
  // terms of this list, so adding an entry changes which rows appear: a metric capable
  // ONLY of Table now shows a row that previously would have been dropped. That is the
  // rule working — the row exists to say what a metric can be — and it is worth naming
  // because the arithmetic in the header (32 of 36 affordances unavailable) was computed
  // over two displays and is now 51 of 54 over three.
  'table',
]

interface AnalyticsDisplayPickerProps {
  /** The METRIC card's id. Variant ids are derived from it, never passed in. */
  cardId: string
  /** For the accessible names — "Add Total Scans as a chart". */
  cardTitle: string
  /** Ids currently on the canvas, so a placed variant shows as removable. */
  placedIds: ReadonlySet<string>
  onAdd: (id: string) => void
  onRemove: (id: string) => void
  /** Notified on `dragstart` / `dragend` so the grid can size its drop placeholder. */
  onDragCardChange: (id: string | null) => void
}

export const AnalyticsDisplayPicker = ({
  cardId,
  cardTitle,
  placedIds,
  onAdd,
  onRemove,
  onDragCardChange,
}: AnalyticsDisplayPickerProps) => {
  const theme = useTheme()

  const capability = useMemo(() => analyticsDisplayCapability(cardId), [cardId])

  const handleDragStart = useCallback(
    (variantId: string) => (e: React.DragEvent) => {
      // Same contract as the row above: react-grid-layout reads the drop through the
      // native HTML5 drag events, and Firefox refuses to start a drag at all unless SOME
      // data is set.
      e.dataTransfer.setData('text/plain', variantId)
      e.dataTransfer.effectAllowed = 'copy'
      onDragCardChange(variantId)
      // The row beneath is ALSO draggable. Without this the press bubbles and the grid is
      // told the metric tile is being dragged, so the drop placeholder is sized 3x1 and
      // the wrong card lands.
      e.stopPropagation()
    },
    [onDragCardChange],
  )

  /**
   * NOTHING TO OFFER -> NO ROW. See the header for why this is the whole row and not the
   * individual chips, and for why it must never be softened to "looks sparse".
   *
   * After the early return rather than before `useMemo`: hooks may not be called
   * conditionally, so the capability has to be computed first and the decision taken on
   * its result.
   */
  if (ALTERNATE_DISPLAYS.every(display => !capability[display].capable)) return null

  return (
    /*
      `mt: 3` — 12px, the house button-group gap (§29) on `@repo/ui`'s 4px base, and the
      WIDEST gap inside a library row. It was `pt: 0.5` (2px), which put the controls the
      same 2px from the metadata above them as the description was from the title, so the
      whole row read as one compressed slab. This is the row's one boundary that changes
      what a thing DOES rather than what it says, and it is now the row's one big gap.

      Owned here rather than by the parent Stack so it travels with the component: the row
      above (`SolutionBadges`) disappears entirely for a metric with no solutions, and a
      parent-owned gap would leave a hole behind it. See the parent's own note.
    */
    <Stack spacing={2} sx={{ mt: 3 }}>
      {/* The label row. Named rather than left implicit: without a lead-in the pills read
          as a second type label on the row, which is what the first pass looked like — the
          reader could not tell whether they described the card or offered another one.
          It owns its own line so that a growing chip count can never be pushed onto a
          second row by the label's width. See the header. */}
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography variant="body3" color="text.secondary">
          Display as
        </Typography>
        {/* VERA-260 — what this control DOES, stated once rather than discovered by trial:
            it places the metric as a chart/map/table, AND (since VRG-467) the same metric
            can be dropped onto an EXISTING chart to plot it alongside another one. No
            glossary term fits ("display as" is a control, not a term of art), so this is
            literal `text` per `InfoTooltip`'s own guidance, not a retyped definition. */}
        <InfoTooltip text="Places this metric on the dashboard as a chart, a map or a table. Drop a metric onto an existing chart to plot it alongside another one." />
      </Stack>

      {/* `useFlexGap` + `gap`, deliberately — see the header. `spacing` compiles to
          `margin-left`, which a wrapped chip carries onto the next line and indents it.
          The value is shared with the solution badges' row so the two read as one rhythm. */}
      <Stack direction="row" useFlexGap flexWrap="wrap" gap={ANALYTICS_CHIP_GAP}>
        {ALTERNATE_DISPLAYS.map(display => {
          const state = capability[display]
          const label = analyticsDisplayLabel(display)
          /*
            ⚠️ THE DEFAULT GROUPING IS APPLIED HERE (VERA-260), not assumed.

            `analyticsVariantCardId(cardId, 'map')` builds the PIN id unconditionally, and
            for a metric only a country fill can draw — an invalid-scan RATE — that is an
            id nothing renders: the chip would be enabled (its capability is the union over
            groupings) and clicking it would place a card that is not in the canvas's card
            list, i.e. a hole. `analyticsDefaultVariantCardId` asks which grouping the
            metric actually opens at and is a no-op for the chart and the table.
          */
          const variantId = analyticsDefaultVariantCardId(cardId, display)
          const enabled = state.capable

          /**
           * ── TWO DIFFERENT QUESTIONS, TWO DIFFERENT BOOLEANS (VERA-260) ────────────────
           *
           * `placedExact` — does THIS metric's own single-metric variant card
           * (`<metricId>::<display>`) exist on the canvas, byte-identical? This is what
           * `onClick` acts on: it is the one card this chip can deterministically add or
           * remove, so clicking always targets it regardless of what else the metric is
           * doing elsewhere. Composed cards have their OWN per-series remove control
           * (`ComposedSeriesLegend`, on the chart itself) — this chip is not a second,
           * ambiguous way to reach the same action.
           *
           * `shownAsDisplay` — is this metric drawn as `display` in ANY canvas card,
           * standalone OR composed (`isMetricDrawnAsDisplay`, walking every id and
           * PARSING it rather than string-comparing it)? This is what the BADGE shows —
           * the fact a reader actually wants from a highlighted chip: "yes, Certificates
           * is on a chart right now," full stop, not "…but only if it has its own card."
           *
           * They intentionally diverge: a metric composed into someone else's chart reads
           * `shownAsDisplay = true, placedExact = false` — highlighted (it IS charted),
           * with a `+` still showing (clicking would add its OWN standalone chart, a
           * separate and still-legitimate thing to want).
           */
          const placedExact = placedIds.has(variantId)
          const shownAsDisplay = isMetricDrawnAsDisplay(cardId, display, placedIds)

          const tooltip = state.capable
            ? placedExact
              ? `Remove ${cardTitle} (${label.toLowerCase()}) from the dashboard`
              : `Add ${cardTitle} as a ${label.toLowerCase()} — drag to place it${shownAsDisplay ? ' (it is already plotted on another chart, and can be on this one too)' : ''}`
            : state.reason

          return (
            /*
              The `<span>` is required, not stylistic. MUI cannot attach a tooltip to a
              disabled interactive element — the browser fires no pointer events on one — so
              the listener has to live on a wrapper. This is the single most important line
              for the incapable case: without it the reason is unreachable and the affordance
              becomes the dead control §46 forbids.
            */
            <Tooltip key={display} title={tooltip}>
              <Box component="span" sx={{ display: 'inline-flex' }}>
                {/*
                  `Box component="button"`, not `Stack` — `Stack`'s prop type does not admit
                  `disabled` / `type`, and the layout wanted here is three inline items, not
                  a stack's spacing machinery.
                */}
                <Box
                  component="button"
                  type="button"
                  disabled={!enabled}
                  /**
                   * ── ALWAYS DRAGGABLE WHEN CAPABLE (VERA-260) — see the header on
                   * `isMetricDrawnAsDisplay` for the concrete complaint this fixes.
                   *
                   * This used to be `enabled && !placed`, which meant a chip already placed
                   * as its own chart could never be dragged again — so a metric already
                   * charted on its own could not also be composed onto a SECOND, different
                   * chart. Dragging is now gated on capability alone; the only remaining
                   * refusal is per-TARGET, in `AnalyticsCanvas.isComposeDropTarget`, which
                   * already blocks dropping onto a card that already carries this metric as
                   * a series — the real duplicate to prevent, not "has this metric been
                   * placed anywhere before."
                   */
                  draggable={enabled}
                  onDragStart={enabled ? handleDragStart(variantId) : undefined}
                  onDragEnd={enabled ? () => onDragCardChange(null) : undefined}
                  onClick={
                    enabled
                      ? () => (placedExact ? onRemove(variantId) : onAdd(variantId))
                      : undefined
                  }
                  aria-label={
                    enabled
                      ? placedExact
                        ? `Remove ${cardTitle} as a ${label.toLowerCase()} from the dashboard`
                        : `Add ${cardTitle} as a ${label.toLowerCase()}`
                      : `${cardTitle} cannot be shown as a ${label.toLowerCase()}`
                  }
                  sx={{
                    /*
                      GEOMETRY from `ANALYTICS_CHIP_SX`, shared with the solution badges
                      four pixels above this row (§36). A pure extraction — the numbers are
                      the ones this chip already shipped — so nothing here moves. STATE
                      stays local, because that is exactly what differs between a control
                      and a badge; see that module's header.

                      §34's selectable-card axis, one size down: TINT = considering, BORDER
                      = chosen. The badge now reads `shownAsDisplay` (drawn as this display
                      ANYWHERE — see the note above) rather than `placedExact`, so a metric
                      only reachable through a composed chart still lights up here.
                    */
                    ...ANALYTICS_CHIP_SX,
                    /*
                      ── OUTLINED AND UNFILLED AT REST: THE HALF THAT SAYS "CONTROL" ─────
                      The solution badges directly above are now FILLED and EDGELESS, and
                      this is the other half of that pair. Both rows used to render this
                      exact treatment — a `primary.100` edge on a transparent ground — so a
                      passive tag and a clickable, draggable control were pixel-identical
                      and a reader had to click one to learn which. Filled-vs-outlined is
                      the distinction now; see `SolutionBadges`' `badgeSx` for the argument.

                      The edge is therefore load-bearing rather than decorative: it is the
                      ONLY thing in the resting state that says this pill is operable.
                    */
                    borderColor: shownAsDisplay ? 'brand.500' : 'primary.100',
                    backgroundColor: shownAsDisplay
                      ? alpha(theme.palette.brand[500], 0.1)
                      : 'transparent',
                    // Disabled reads as metadata, not as a control that failed. It keeps its
                    // outline so the row's rhythm is identical whichever answer a metric
                    // gets — a missing pill would make the capable rows look longer rather
                    // than better-supported.
                    color: enabled ? 'text.secondary' : 'text.disabled',
                    /*
                      `grab` FOR EVERY ENABLED CHIP, including a placed one. It was
                      `placedExact ? 'pointer' : 'grab'`, which is a leftover from when a
                      placed chip genuinely could not be dragged again — `draggable` has
                      been gated on capability alone since VERA-260 (see the note on
                      `draggable` below), so `pointer` was telling a reader they could only
                      click something they could in fact still drag onto another chart.
                      Three chips in one row showing two different cursors also made the
                      row's own affordance unreadable.
                    */
                    cursor: enabled ? 'grab' : 'not-allowed',
                    '&:active': enabled ? { cursor: 'grabbing' } : undefined,
                    // Hover feedback stays keyed to `shownAsDisplay`, not `placedExact`: a
                    // chip already highlighted (chosen, static) keeps its own persistent
                    // tint rather than a hover ripple LIGHTENING it — the same call the
                    // "already added" row above makes for the identical reason.
                    '&:hover':
                      enabled && !shownAsDisplay
                        ? { backgroundColor: alpha(theme.palette.brand[500], 0.06) }
                        : undefined,
                    /*
                      ── THE GRIP, REVEALED ON ENGAGEMENT ────────────────────────────────
                      Hover or keyboard focus SWAPS the leading category glyph for a grip.
                      Drag was the least discoverable thing on this row: the cursor only
                      says `grab` once you are already over the chip, and the tooltip's
                      "drag to place it" is prose you have to stop and read.

                      ⚠️ A SWAP, not an addition, and that is the whole reason it can exist
                      at all. A grip APPENDED to the chip costs ~12px of width; three chips
                      in a row whose widest arrangement already measures 234.8 of a 317px
                      column would push the row to two lines on hover — content moving under
                      the pointer, which is worse than the problem. Reserving the space
                      permanently costs the same width on every row for a mark that is only
                      meaningful under the cursor. Swapping costs nothing and shifts nothing:
                      both marks sit in one 13px box, one visible at a time.

                      The category glyph is what is given up, and it is the cheap thing to
                      give up — the chip's LABEL ("Chart") carries the same identity, in
                      words, and it never moves. `:focus-visible` is included so a keyboard
                      user is told the same thing; the tooltip carries it for a screen
                      reader, and the grip itself is `aria-hidden` decoration.
                    */
                    '&:hover .vera-chip-glyph, &:focus-visible .vera-chip-glyph': {
                      opacity: enabled ? 0 : 1,
                    },
                    '&:hover .vera-chip-grip, &:focus-visible .vera-chip-grip': { opacity: 1 },
                    // (`'& svg': { flexShrink: 0 }` now comes from `ANALYTICS_CHIP_SX`.)
                  }}
                >
                  {/*
                    One 13px box holding both marks stacked, so the swap above is a pure
                    cross-fade with no reflow. `position: relative` + absolutely-positioned
                    children is what makes the box's size independent of which mark shows.
                  */}
                  <Box
                    aria-hidden
                    sx={{
                      position: 'relative',
                      display: 'inline-flex',
                      width: 13,
                      height: 13,
                      flexShrink: 0,
                      '& > span': {
                        position: 'absolute',
                        inset: 0,
                        display: 'inline-flex',
                        transition: 'opacity 120ms ease',
                      },
                    }}
                  >
                    {/* The ONE category->glyph table (§36), consumed rather than re-picked.
                        A second mapping here is how Chart ends up with a different mark in
                        the picker than on the card it creates. */}
                    <Box component="span" className="vera-chip-glyph">
                      <AnalyticsCategoryGlyph category={display} size={13} color="inherit" />
                    </Box>
                    {/* Only for a chip that can actually be dragged. Promising a grip on an
                        incapable chip would be the "affordance that lies" this row's header
                        spends its length avoiding. */}
                    {enabled && (
                      <Box component="span" className="vera-chip-grip" sx={{ opacity: 0 }}>
                        <GripVerticalIcon size={13} strokeWidth={1.5} />
                      </Box>
                    )}
                  </Box>
                  <Box component="span">{label}</Box>
                  {placedExact && <XIcon size={12} strokeWidth={1.5} />}
                </Box>
              </Box>
            </Tooltip>
          )
        })}
      </Stack>
    </Stack>
  )
}
